import {Children, cloneElement, useCallback, useEffect, useId, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactElement, type ReactNode} from 'react';
import '../styles/feature-showcase.css';

const MOBILE_WIDTH = 640;
const SLIDE_DURATION = 3000; // Same cadence as the site's existing sliders.
const labels = ['Özel Oranlar', 'Çevir Kazan', 'Freespin Paketleri', 'Canlı Yayın'];

export default function FeatureShowcase({children}: {children: ReactNode}) {
  const slides = Children.toArray(children) as ReactElement<HTMLAttributes<HTMLElement>>[];
  const root = useRef<HTMLDivElement>(null), track = useRef<HTMLDivElement>(null);
  const [mobile, setMobile] = useState(false), [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false), [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false), [touching, setTouching] = useState(false);
  const [visible, setVisible] = useState(true), [inView, setInView] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false), [cycle, setCycle] = useState(0);
  const id = useId();

  useEffect(() => {
    const element = root.current!;
    const resize = new ResizeObserver(([entry]) => setMobile(entry.contentRect.width < MOBILE_WIDTH));
    resize.observe(element);
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {threshold: 0.1});
    observer.observe(element);
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(motion.matches);
    const updateVisibility = () => setVisible(document.visibilityState === 'visible');
    updateMotion(); updateVisibility();
    motion.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      resize.disconnect(); observer.disconnect(); motion.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => {
    // A viewport change must not leave desktop columns horizontally displaced.
    const scroller = track.current;
    if (!scroller) return;
    if (!mobile) {scroller.scrollLeft = 0; setActive(0);}
    else {
      const slide = scroller.children[active] as HTMLElement | undefined;
      const first = scroller.firstElementChild as HTMLElement | null;
      if (slide && first) scroller.scrollLeft = slide.offsetLeft - first.offsetLeft;
    }
  }, [mobile]);

  const move = useCallback((index: number) => {
    const scroller = track.current;
    if (!scroller || !mobile) return;
    const next = (index + slides.length) % slides.length;
    const first = scroller.firstElementChild as HTMLElement;
    const slide = scroller.children[next] as HTMLElement;
    scroller.scrollTo({left: slide.offsetLeft - first.offsetLeft,
      behavior: reducedMotion || Math.abs(next - active) > 1 ? 'auto' : 'smooth'});
    setCycle(value => value + 1);
  }, [mobile, slides.length, active, reducedMotion]);
  const rotationEnabled = !paused && !focused && !reducedMotion;
  const playing = mobile && rotationEnabled && !hovered && !touching && visible && inView;
  useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(() => move(active + 1), SLIDE_DURATION);
    return () => clearTimeout(timer);
  }, [playing, active, cycle, move]);

  const syncSlide = () => {
    const scroller = track.current;
    if (!mobile || !scroller) return;
    const first = scroller.firstElementChild as HTMLElement;
    let nearest = 0, distance = Infinity;
    Array.from(scroller.children).forEach((child, index) => {
      const gap = Math.abs((child as HTMLElement).offsetLeft - first.offsetLeft - scroller.scrollLeft);
      if (gap < distance) {nearest = index; distance = gap;}
    });
    setActive(nearest);
  };
  const select = (index: number) => {setPaused(true); move(index);};

  return <div ref={root} className="feature-showcase" data-mobile={mobile} data-playing={playing} style={{'--feature-count':slides.length} as CSSProperties}
    role={mobile ? 'region' : undefined} aria-roledescription={mobile ? 'slayt gösterisi' : undefined}
    aria-label={mobile ? 'TRWIN Extra alanları' : undefined}
    onFocusCapture={event => {if (!(event.target as Element).closest('[data-rotation-toggle]')) setFocused(true);}}
    onPointerEnter={event => {if (event.pointerType === 'mouse') setHovered(true);}}
    onPointerLeave={() => setHovered(false)}>
    <div id={id} ref={track} className="feature-grid" onScroll={syncSlide}
      onPointerDown={() => setTouching(true)} onPointerUp={() => {setTouching(false); setCycle(value => value + 1);}}
      onPointerCancel={() => setTouching(false)}>
      {slides.map((slide, index) => cloneElement(slide, {
        role: mobile ? 'group' : undefined,
        'aria-roledescription': mobile ? 'slayt' : undefined,
        'aria-label': mobile ? `${labels[index]} · ${index + 1} / ${slides.length}` : undefined,
        'aria-hidden': mobile && index !== active ? true : undefined,
        inert: mobile && index !== active,
      }))}
    </div>
    {mobile && <div className="feature-slider-controls">
      <div className="feature-slider-row">
        <button type="button" className="feature-slider-arrow" aria-label="Önceki kart" aria-controls={id} onClick={() => select(active - 1)}>‹</button>
        <div className="feature-slider-dots" aria-label="Kart seçimi">
          {slides.map((slide, index) => <button key={slide.key ?? index} type="button"
            aria-label={`${labels[index]} kartına git`} aria-current={index === active ? 'true' : undefined}
            aria-controls={id} onClick={() => select(index)}><span/></button>)}
        </div>
        <span className="feature-slider-count" aria-live={playing ? 'off' : 'polite'} aria-atomic="true">{active + 1} / {slides.length}</span>
        {!reducedMotion && <button type="button" data-rotation-toggle className="feature-slider-play" aria-label={rotationEnabled ? 'Otomatik geçişi duraklat' : 'Otomatik geçişi başlat'}
          onClick={() => {setPaused(rotationEnabled); setFocused(false); setCycle(value => value + 1);}}>{rotationEnabled ? 'Ⅱ' : '▶'}</button>}
        <button type="button" className="feature-slider-arrow" aria-label="Sonraki kart" aria-controls={id} onClick={() => select(active + 1)}>›</button>
      </div>
      <div className="feature-slider-progress" aria-hidden="true"><span key={`${active}-${cycle}-${playing}`} style={{animationDuration: `${SLIDE_DURATION}ms`}}/></div>
    </div>}
  </div>;
}
